'use client';

import { useContext, useEffect, useRef } from 'react';
import useMarketStateStore, {
	MarketStateStore,
	MarketState,
	MarketListeningSelection,
} from '../../stores/useMarketStateStore';
import useInterval from '../useInterval';
import { OrderSubscriberContext } from '../../providers/orderSubscriberProvider';
import { useCurrentSlot } from 'src/hooks/useCurrentSlot';
import useOraclePriceStore from '../../stores/useOraclePriceStore';
import { COMMON_MATH, ENUM_UTILS, MarketId } from '@drift/common';
import {
	BN,
	BigNum,
	DEFAULT_TOP_OF_BOOK_QUOTE_AMOUNTS,
	DriftClient,
	L2Level,
	L2OrderBook,
	MarketType,
	MMOraclePriceData,
	PERCENTAGE_PRECISION_EXP,
	PRICE_PRECISION_EXP,
	PerpMarketAccount,
	PhoenixSubscriber,
	QUOTE_PRECISION_EXP,
	SerumSubscriber,
	ZERO,
	getVammL2Generator,
} from '@drift-labs/sdk';
import useDriftClient from '../useDriftClient';
import { sortBnAsc, sortBnDesc } from '../../utils/math';
import useMarketsForDlobToTrack from '../useMarketsForDlobToTrack';
import useSyncPhoenixSubscriberList from './useSyncPhoenixSubscriberList';
import useSyncSerumSubscriberList from './useSyncSerumSubscriberList';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import { PageRoute, ZERO_PRICE_BIGNUM } from '../../constants/constants';
import { useUncrossOrderbook } from './useUncrossOrderbook';
import useDevStore from '../../stores/useDevStore';
import { DriftWindow } from 'src/window/driftWindow';
import useOrderbookDisplayStore, {
	OrderbookSource,
} from 'src/stores/orderbook/useOrderbookDisplayStore';
import { useQueryClient } from '@tanstack/react-query';
import { orderbookQueryKeys } from 'src/hooks/query/orderbook/orderbookQueryKeys';
import { L2OrderbookWithSlot } from 'src/hooks/query/orderbook/types';
import { PRICING_QUERY_KEY } from 'src/hooks/query/pricing/usePricingData';
import { PricingData } from 'src/hooks/query/pricing/types';
import usePricingSourceStore from 'src/stores/pricing/usePricingSourceStore';

const DLOB_REFRESH_RATE_MS = 1000;
// rate of refresh to use for any pages besides trade and overview (where users can trade from) - can play w this
const LAZY_DLOB_REFRESH_RATE_MS = 20000;

const DEFAULT_L2_STATE: L2OrderBook = { asks: [], bids: [] };
const DEFAULT_ORDERBOOK_ROWS = 20;

const getVammL2ForMarket = (
	marketType: MarketType,
	perpMarketAccount: PerpMarketAccount,
	priceData: MMOraclePriceData,
	groupingSize: number
): L2OrderBook => {
	if (!ENUM_UTILS.match(marketType, MarketType.PERP) || !perpMarketAccount)
		return DEFAULT_L2_STATE;

	const vammL2Generator = getVammL2Generator({
		marketAccount: perpMarketAccount,
		mmOraclePriceData: priceData,
		numOrders: groupingSize * DEFAULT_ORDERBOOK_ROWS,
		topOfBookQuoteAmounts: DEFAULT_TOP_OF_BOOK_QUOTE_AMOUNTS,
	});

	const vammAsksGenerator = vammL2Generator.getL2Asks();
	const vammBidsGenerator = vammL2Generator.getL2Bids();

	const vammL2 = {
		asks: Array.from(vammAsksGenerator),
		bids: Array.from(vammBidsGenerator),
	};

	return vammL2;
};

/**
 * Merge seperate sources of l2 orderbook together. During the merge it will sort by price (ascending) and merge sources where they share the same price
 * @param l2Data
 * @returns
 */
const mergeL2Data = (l2Data: L2OrderBook[]): L2OrderBook => {
	// Buckets to put bids and asks into when merging
	const askBuckets = new Map<string, L2Level>();
	const bidBuckets = new Map<string, L2Level>();

	const addOrAppendToBucketStorage = (
		key: string,
		currentL2Level: L2Level,
		bucketStorage: Map<string, L2Level>
	) => {
		const currentBucket = bucketStorage.get(key);

		if (currentBucket) {
			currentBucket.size = currentBucket.size.add(currentL2Level.size);
			currentBucket.sources = {
				...currentBucket.sources,
				...currentL2Level.sources,
			};
		} else {
			bucketStorage.set(key, currentL2Level);
		}
	};

	// Place all the bids and asks into the buckets
	for (const bid of l2Data.map((d) => d.bids).flat()) {
		const bidKey = bid.price.toString();
		addOrAppendToBucketStorage(bidKey, bid, bidBuckets);
	}
	for (const ask of l2Data.map((d) => d.asks).flat()) {
		const askKey = ask.price.toString();
		addOrAppendToBucketStorage(askKey, ask, askBuckets);
	}

	// Map the bids and asks back out into l2 orderbook
	const l2Orderbook = {
		bids: Array.from(bidBuckets.values()).sort((bidA, bidB) =>
			sortBnDesc(bidA.price, bidB.price)
		),
		asks: Array.from(askBuckets.values()).sort((askA, askB) =>
			sortBnAsc(askA.price, askB.price)
		),
	};

	return l2Orderbook;
};

// Use a const here so that it's the same value between refreshes
const BLANK_MARKETS_TO_TRACK = {};

/**
 * Gets merged L2 orderbook state for PERP markets (VAMM + DLOB)
 */
export const getPerpMarketL2State = (
	marketsToGetDlobFor: MarketId[],
	newDlob: any,
	currentSlot: number,
	driftClient: DriftClient
): L2OrderBook[] => {
	return marketsToGetDlobFor.map((mkt) => {
		if (!ENUM_UTILS.match(mkt.marketType, MarketType.PERP)) {
			return { bids: [], asks: [] };
		}

		const mmOraclePriceData = driftClient.getMMOracleDataForPerpMarket(
			mkt.marketIndex
		);

		if (!mmOraclePriceData) {
			return { bids: [], asks: [] };
		}

		// Get VAMM L2 state
		const perpMarketAccount = driftClient?.getPerpMarketAccount(
			mkt.marketIndex
		);

		if (!perpMarketAccount) {
			return { bids: [], asks: [] };
		}

		const vammL2 = getVammL2ForMarket(
			mkt.marketType,
			perpMarketAccount,
			mmOraclePriceData,
			1
		);

		// Get DLOB L2 state
		const bidsGenerator = newDlob.getRestingLimitBids(
			mkt.marketIndex,
			currentSlot,
			mkt.marketType,
			mmOraclePriceData
		);

		const asksGenerator = newDlob.getRestingLimitAsks(
			mkt.marketIndex,
			currentSlot,
			mkt.marketType,
			mmOraclePriceData
		);

		const dlobBids: L2Level[] = Array.from(bidsGenerator).map((node: any) => {
			const size = node.order
				? node.order.baseAssetAmount.sub(node.order.baseAssetAmountFilled)
				: ZERO;
			return {
				price: node.getPrice(mmOraclePriceData, currentSlot),
				size,
				sources: { dlob: size },
			};
		});

		const dlobAsks: L2Level[] = Array.from(asksGenerator).map((node: any) => {
			const size = node.order
				? node.order.baseAssetAmount.sub(node.order.baseAssetAmountFilled)
				: ZERO;

			return {
				price: node.getPrice(mmOraclePriceData, currentSlot),
				size,
				sources: { dlob: size },
			};
		});

		const dlobL2 = { bids: dlobBids, asks: dlobAsks };

		// Merge VAMM and DLOB
		return mergeL2Data([vammL2, dlobL2]);
	});
};

/**
 * Gets merged L2 orderbook state for SPOT markets (DLOB + Phoenix + Serum)
 */
export const getSpotMarketL2State = (
	marketsToGetDlobFor: MarketId[],
	newDlob: any,
	currentSlot: number,
	getPhoenixSubscriber: (mkt: MarketId) => PhoenixSubscriber,
	getSerumSubscriber: (mkt: MarketId) => SerumSubscriber,
	driftClient: DriftClient
): L2OrderBook[] => {
	return marketsToGetDlobFor.map((mkt) => {
		if (!ENUM_UTILS.match(mkt.marketType, MarketType.SPOT)) {
			return { bids: [], asks: [] };
		}

		// Skip USDC market
		if (mkt.marketIndex === 0) {
			return { bids: [], asks: [] };
		}

		const oraclePriceData = driftClient.getOracleDataForSpotMarket(
			mkt.marketIndex
		);

		if (!oraclePriceData) {
			return { bids: [], asks: [] };
		}

		// Get DLOB L2 state
		const bidsGenerator = newDlob.getRestingLimitBids(
			mkt.marketIndex,
			currentSlot,
			mkt.marketType,
			oraclePriceData
		);

		const asksGenerator = newDlob.getRestingLimitAsks(
			mkt.marketIndex,
			currentSlot,
			mkt.marketType,
			oraclePriceData
		);

		const dlobBids: L2Level[] = Array.from(bidsGenerator).map((node: any) => {
			const size = node.order
				? node.order.baseAssetAmount.sub(node.order.baseAssetAmountFilled)
				: ZERO;
			return {
				price: node.getPrice(oraclePriceData, currentSlot),
				size,
				sources: { dlob: size },
			};
		});

		const dlobAsks: L2Level[] = Array.from(asksGenerator).map((node: any) => {
			const size = node.order
				? node.order.baseAssetAmount.sub(node.order.baseAssetAmountFilled)
				: ZERO;

			return {
				price: node.getPrice(oraclePriceData, currentSlot),
				size,
				sources: { dlob: size },
			};
		});

		const dlobL2 = { bids: dlobBids, asks: dlobAsks };

		// Get Phoenix L2 state
		let phoenixL2: L2OrderBook = { bids: [], asks: [] };
		try {
			const phoenixSubscriber = getPhoenixSubscriber(mkt);
			if (phoenixSubscriber) {
				const bids = Array.from(phoenixSubscriber.getL2Bids()) as L2Level[];
				const asks = Array.from(phoenixSubscriber.getL2Asks()) as L2Level[];
				phoenixL2 = { bids, asks };
			}
		} catch (e) {
			console.log('error getting phoenix l2 state', e);
		}

		// Get Serum L2 state
		let serumL2: L2OrderBook = { bids: [], asks: [] };
		try {
			const serumSubscriber = getSerumSubscriber(mkt);
			if (serumSubscriber) {
				const bids = Array.from(serumSubscriber.getL2Bids()) as L2Level[];
				const asks = Array.from(serumSubscriber.getL2Asks()) as L2Level[];
				serumL2 = { bids, asks };
			}
		} catch (e) {
			console.log('error getting serum l2 state', e);
		}

		// Merge DLOB, Phoenix, and Serum
		return mergeL2Data([dlobL2, phoenixL2, serumL2]);
	});
};

/**
 * Handles turning on the blockchain order listener and keeping it in sync with the DLOB class when the dlob listening gets enabled.
 * @param enabled
 */
const useSyncMarketDataWithBlockchain = () => {
	const queryClient = useQueryClient();
	const setMarketStateStore = useMarketStateStore((s) => s.set);
	const orderSubscriber = useContext(OrderSubscriberContext)?.orderSubscriber;
	const currentSlot = useCurrentSlot();
	const getMarketPriceData = useOraclePriceStore((s) => s.getMarketPriceData);
	const driftClient = useDriftClient();
	const marketsToTrack = useMarketsForDlobToTrack(
		MarketListeningSelection.BLOCKCHAIN
	);
	const shouldBeUsedForOrderbook = useOrderbookDisplayStore(
		(s) => s.orderbookSource === OrderbookSource.BLOCKCHAIN
	);
	const setOrderbookStoreState = useOrderbookDisplayStore((s) => s.set);
	const uncrossDisabled_ = useDevStore((s) => s.dlobSettings.disableUncross);
	const uncrossDisabled = useRef(uncrossDisabled_);
	useEffect(() => {
		uncrossDisabled.current = uncrossDisabled_;
	}, [uncrossDisabled_]);

	const uncross = useUncrossOrderbook();

	const currentPage = useDriftStore((s) => s.currentPageRoute);
	const refreshRateToUse =
		!currentPage ||
		ENUM_UTILS.match(currentPage, PageRoute.portfolio) ||
		ENUM_UTILS.match(currentPage, PageRoute.trade)
			? DLOB_REFRESH_RATE_MS
			: LAZY_DLOB_REFRESH_RATE_MS;
	const bulkAccountLoader = useDriftStore((s) => s.currentBulkAccountLoader);

	const getPhoenixSubscriber = useMarketStateStore(
		(s) => s.getPhoenixSubscriberForMarket
	);
	const getSerumSubscriber = useMarketStateStore(
		(s) => s.getSerumSubscriberForMarket
	);

	const enabled = marketsToTrack.totalMarkets > 0;

	const allMarketsToTrack = marketsToTrack.allMarkets;

	// If not enabled then pass a blank list of markets to track
	useSyncPhoenixSubscriberList(
		enabled ? marketsToTrack.categories : BLANK_MARKETS_TO_TRACK
	);
	useSyncSerumSubscriberList(enabled ? marketsToTrack : BLANK_MARKETS_TO_TRACK);

	useEffect(() => {
		if (!orderSubscriber) return;

		if (enabled) {
			// Turn the blockchain listener on
			orderSubscriber.subscribe();
		} else {
			// Turn the blockchain listener off
			orderSubscriber.usersAccounts?.clear();
			orderSubscriber.unsubscribe();
		}

		return () => {
			// Unsubscribe at end of hook to be safe
			orderSubscriber.usersAccounts?.clear();
			orderSubscriber.unsubscribe();
		};
	}, [enabled, orderSubscriber]);

	useInterval(() => {
		if (!enabled) return;

		if (DriftWindow.BREAK_BLOCKCHAIN_DLOB_FETCHING) {
			// Dev setting for testing fallback logic
			return;
		}

		(async () => {
			if (!orderSubscriber || !driftClient) return;

			// All markets except USDC SPOT market
			const marketsToGetDlobFor = allMarketsToTrack;

			const newDlob = await orderSubscriber.getDLOB(currentSlot);

			const newL2State: MarketStateStore['marketDataState'] = {};

			// Get merged L2 state for PERP markets
			const perpL2State = getPerpMarketL2State(
				marketsToGetDlobFor,
				newDlob,
				currentSlot,
				driftClient
			);

			// Get merged L2 state for SPOT markets
			const spotL2State = getSpotMarketL2State(
				marketsToGetDlobFor,
				newDlob,
				currentSlot,
				getPhoenixSubscriber,
				getSerumSubscriber,
				driftClient
			);

			// Combine results based on market type
			const mergedDlobState = marketsToGetDlobFor.map((mkt, index) => {
				if (ENUM_UTILS.match(mkt.marketType, MarketType.PERP)) {
					return perpL2State[index];
				} else {
					return spotL2State[index];
				}
			});

			// Fill the new L2 state with the merged data
			marketsToGetDlobFor.forEach((mktId, index) => {
				const dlobStateForMarket = mergedDlobState[index];

				const oraclePriceStoreData = getMarketPriceData(mktId);

				if (!oraclePriceStoreData) return;

				// TODO : This method loops through all orders, should be able to assume that they are sorted here and just pick the ones off the top (applies to the other places this is used too)
				const spreadBidAskMark = COMMON_MATH.calculateSpreadBidAskMark(
					dlobStateForMarket,
					oraclePriceStoreData?.rawPriceData?.price
				);

				const orderbookToUse = uncrossDisabled.current
					? dlobStateForMarket
					: uncross.uncrossL2Orderbook(
							dlobStateForMarket,
							oraclePriceStoreData.rawPriceData,
							mktId
						);

				const newMarketStateDate: MarketState = {
					marketId: mktId,
					orderbook: orderbookToUse,
					oracle: {
						price: BigNum.fromPrint(
							oraclePriceStoreData.priceData.price.toString(),
							PRICE_PRECISION_EXP
						),
						slot: new BN(oraclePriceStoreData.priceData.slot),
						confidence: new BN(oraclePriceStoreData.priceData.confidence),
						hasSufficientNumberOfDataPoints:
							oraclePriceStoreData.priceData.hasSufficientNumberOfDataPoints,
						twap: oraclePriceStoreData.priceData.twap
							? BigNum.from(
									oraclePriceStoreData.priceData.twap,
									PRICE_PRECISION_EXP
								)
							: undefined,
						twapConfidence: oraclePriceStoreData.priceData.twapConfidence
							? new BN(oraclePriceStoreData.priceData.twapConfidence)
							: undefined,
						maxPrice: oraclePriceStoreData.priceData.maxPrice
							? BigNum.fromPrint(
									oraclePriceStoreData.priceData.maxPrice.toString(),
									PRICE_PRECISION_EXP
								)
							: ZERO_PRICE_BIGNUM,
					},
					derivedState: {
						markPrice: BigNum.from(
							spreadBidAskMark.markPrice,
							PRICE_PRECISION_EXP
						),
						bestBid: BigNum.from(
							spreadBidAskMark.bestBidPrice,
							PRICE_PRECISION_EXP
						),
						bestAsk: BigNum.from(
							spreadBidAskMark.bestAskPrice,
							PRICE_PRECISION_EXP
						),
						updateSlot: bulkAccountLoader?.mostRecentSlot ?? 0,
					},
				};

				newL2State[mktId.key] = newMarketStateDate;
			});

			// Update the store state with the new L2 state
			setMarketStateStore((s) => {
				for (const market of marketsToGetDlobFor) {
					s.marketDataState[market.key] = newL2State[market.key];
				}
				s.latestUpdateSlot = Math.max(
					s.latestUpdateSlot,
					bulkAccountLoader?.mostRecentSlot ?? 0
				);
			});

			// Update React Query pricing cache with blockchain-derived pricing data.
			// This hook only runs when orderbookSource is BLOCKCHAIN, which means
			// DLOB server is already down. We additionally check that the pricing
			// WebSocket is not active to avoid overwriting fresher WS data.
			// Effective condition: DLOB dead (implicit) + WebSocket dead (explicit).
			const pricingSource = usePricingSourceStore.getState().source;
			if (pricingSource !== 'websocket') {
				const currentPricingMap = new Map(
					queryClient.getQueryData<Map<string, PricingData>>(
						PRICING_QUERY_KEY
					) ?? new Map<string, PricingData>()
				);

				for (const mktId of marketsToGetDlobFor) {
					const marketState = newL2State[mktId.key];
					if (!marketState) continue;

					currentPricingMap.set(mktId.key, {
						symbol: mktId.key,
						marketIndex: mktId.marketIndex,
						marketType: mktId.marketType,
						oraclePrice: marketState.oracle.price.toNum(),
						markPrice: marketState.derivedState.markPrice.toNum(),
						lastPrice: marketState.derivedState.markPrice.toNum(),
						updateTimestamp: Date.now(),
					});
				}

				queryClient.setQueryData(PRICING_QUERY_KEY, currentPricingMap);
			}

			if (shouldBeUsedForOrderbook) {
				const currentMarketId = useOrderbookDisplayStore.getState().marketId;
				const selectedMarketData = Object.values(newL2State).find(
					(marketState) =>
						currentMarketId
							? marketState.marketId.equals(currentMarketId)
							: false
				);

				if (selectedMarketData) {
					const marketKey = selectedMarketData.marketId.key;
					const l2OrderbookWithSlot: L2OrderbookWithSlot = {
						...selectedMarketData.orderbook,
						slot: bulkAccountLoader?.mostRecentSlot ?? 0,
					};

					const oraclePriceData = selectedMarketData.marketId.isPerp
						? driftClient.getOracleDataForPerpMarket(
								selectedMarketData.marketId.marketIndex
							)
						: driftClient.getOracleDataForSpotMarket(
								selectedMarketData.marketId.marketIndex
							);

					if (oraclePriceData) {
						const spreadBidAskMark = COMMON_MATH.calculateSpreadBidAskMark(
							selectedMarketData.orderbook,
							oraclePriceData?.price
						);

						queryClient.setQueryData(
							orderbookQueryKeys.l2Orderbook(marketKey),
							l2OrderbookWithSlot
						);
						queryClient.setQueryData(
							orderbookQueryKeys.marketPriceState(marketKey),
							{
								bestAsk: selectedMarketData.derivedState.bestAsk,
								bestBid: selectedMarketData.derivedState.bestBid,
								markPrice: selectedMarketData.derivedState.markPrice,
								spreadPct: BigNum.from(
									spreadBidAskMark.spreadPct,
									PERCENTAGE_PRECISION_EXP
								),
								spreadQuote: BigNum.from(
									spreadBidAskMark.spreadQuote,
									QUOTE_PRECISION_EXP
								),
								slot: selectedMarketData.marketSlot,
							}
						);

						setOrderbookStoreState((s) => {
							s.loading = false;
						});
					}
				}
			}
		})();
	}, refreshRateToUse);
};

export default useSyncMarketDataWithBlockchain;
